import { AudioPlayContext } from '@/contexts/AudioPlayContext';
import { GlobalDataContext } from '@/contexts/GlobalDataContext';
import { ScreenContext } from '@/contexts/ScreenContext';
import { BetState } from '@/models/games/enums/BetState';
import BScroll from '@better-scroll/core';
import { BScrollConstructor } from '@better-scroll/core/dist/types/BScroll';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { IconButton, IconButtonProps } from '@mui/material';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GameType as GameTypeEnum } from '../../../../../models/games/enums/GameType';
import { RootState } from '../../../../../store/store';
import { AmountText } from '../../../../../utils/formatter';
import i18n from '../../../../locales/i18n';
import { getMainUserState } from '../../../../main/selector';
import { Bet } from '../../../../report/slice/betRecord';
import { DetailListDivider } from '../../ReportDivider';
import { AndarBaharResultDetailPanel } from './AndarBaharResultDetailPanel';
import { AndarBaharResultRecordPanel } from './AndarBaharResultRecordPanel';
import { BaccGameResultRecordPanel } from './BaccGameResultRecordPanel';
import { BlackjackDetailContentRemark } from './BlackjackDetailContentRemark';
import { BlackjackResultDetailPanel } from './BlackjackResultDetailPanel';
import { BlackjackResultRecordPanel } from './BlackjackResultRecordPanel';
import { DragonTigerResultRecordPanel } from './DragonTigerResultRecordPanel';
import { FishPrawnCrabResultRecordPanel } from './FishPrawnCrabResultRecordPanel';
import './index.scss';
import { Playback } from './playback';
import { PokDengGameResultRecordPanel } from './PokDengGameResultRecordPanel';
import { RouletteResultRecordPanel } from './RouletteGameResultRecordPanel';
import { SedieGameResultRecordPanel } from './SedieGameResultRecordPanel';
import { SicBoResultRecordPanel } from './SicBoResultRecordPanel';
import {
    TeenPattiResultDetailPanel,
    TeenPattiResultRecordPanel,
} from './TeenPattiResultRecordPanel';
import { UltraRouletteResultRecordPanel } from './UltraRouletteResultRecordPanel';

type DetailData = {
    title: string;
    content: JSX.Element;
    lang_direction?: boolean | undefined;
};

type DetailProps = {
    onGoing: boolean;
    bet: Bet;
    gameName: string;
    isFailed?: boolean;
};

const isMessageExist = (key: string) => {
    return i18n.exists(key);
};

export const RoundText = ({ bet, gameName }: DetailProps) => {
    const { i18n } = useTranslation();
    const languageDir = useMemo(() => {
        return i18n.dir(i18n.language);
    }, [i18n.language]);

    switch (bet.GameType) {
        case GameTypeEnum.SeDie:
        case GameTypeEnum.TeenPatti2020:
        case GameTypeEnum.AndarBahar:
        case GameTypeEnum.Roulette:
        case GameTypeEnum.UltraRoulette:
        case GameTypeEnum.SicBo:
        case GameTypeEnum.ThaiHiLo:
        case GameTypeEnum.FishPrawnCrab:
            return (
                <>
                    {languageDir == 'rtl'
                        ? `${bet.Round} / ${gameName}`
                        : `${gameName} / ${bet.Round}`}
                </>
            );
        default:
            return (
                <>
                    {languageDir == 'rtl'
                        ? `${bet.Shoes}-${bet.Round} / ${gameName}`
                        : `${gameName} / ${bet.Shoes}-${bet.Round}`}
                </>
            );
    }
};

type DetailResultListItemProps = {
    titleId: string;
    onGoing: boolean;
    children: JSX.Element;
    langDirection?: boolean | undefined;
    bet: Bet;
};
const DetailResultListItem = (props: DetailResultListItemProps) => {
    const { bet, titleId, onGoing, langDirection, children } = props;
    const [detailPanel, setDetailPanel] = useState(<></>);
    const { t } = useTranslation();
    useEffect(() => {
        switch (bet.GameType) {
            case GameTypeEnum.TeenPatti2020:
                setDetailPanel(<TeenPattiResultDetailPanel bet={bet} />);
                break;
            case GameTypeEnum.AndarBahar:
                setDetailPanel(<AndarBaharResultDetailPanel bet={bet} />);
                break;
            case GameTypeEnum.Blackjack:
                setDetailPanel(<BlackjackResultDetailPanel bet={bet} />);
                break;
            default:
                setDetailPanel(<></>);
                break;
        }
    }, [bet]);
    return (
        <>
            <DetailListItem
                titleId={titleId}
                setLangDirection={langDirection}
                onGoing={onGoing}
            >
                {children}
            </DetailListItem>
            {titleId === t('betlog.result') && !onGoing && detailPanel}
        </>
    );
};
const BetContentRemark = ({ bet, isFailed = false }: DetailProps) =>
    bet.GameType === GameTypeEnum.Blackjack ? (
        <BlackjackDetailContentRemark bet={bet} />
    ) : isFailed ? (
        <DetailContent gameType={bet.GameType} betType={bet.BetType} />
    ) : (
        <DetailContentRemark
            gameType={bet.GameType}
            betType={bet.BetType}
            state={bet.State}
        />
    );

const BetRemark = ({ reason }: { reason: string | undefined }) => {
    const { t } = useTranslation();
    let errorMsg = `abc_failure_betlog.error_code.${reason}`;
    if (!isMessageExist(errorMsg)) {
        errorMsg = 'abc_failure_betlog.error_common';
        return <>{t(errorMsg).replace('{{errorcode}}', reason ?? '')}</>;
    } else {
        return <>{t(errorMsg)}</>;
    }
};

const ResultPanel = (props: DetailProps) => {
    const { onGoing, bet } = props;
    const { t } = useTranslation();
    if (!onGoing) {
        let child = <></>;
        switch (bet.GameType) {
            case GameTypeEnum.Baccarat:
            case GameTypeEnum.SqueezeBaccarat:
                child = <BaccGameResultRecordPanel bet={bet} />;
                break;
            case GameTypeEnum.SeDie:
                child = <SedieGameResultRecordPanel bet={bet} />;
                break;
            case GameTypeEnum.PokDeng:
                child = <PokDengGameResultRecordPanel bet={bet} />;
                break;
            case GameTypeEnum.Dragon:
                child = <DragonTigerResultRecordPanel bet={bet} />;
                break;
            case GameTypeEnum.SicBo:
            case GameTypeEnum.ThaiHiLo:
                child = <SicBoResultRecordPanel bet={bet} />;
                break;
            case GameTypeEnum.TeenPatti2020:
                child = <TeenPattiResultRecordPanel bet={bet} />;
                break;
            case GameTypeEnum.AndarBahar:
                child = <AndarBaharResultRecordPanel bet={bet} />;
                break;
            case GameTypeEnum.Roulette:
                child = <RouletteResultRecordPanel bet={bet} />;
                break;
            case GameTypeEnum.UltraRoulette:
                child = <UltraRouletteResultRecordPanel bet={bet} />;
                break;
            case GameTypeEnum.Blackjack:
                child = <BlackjackResultRecordPanel />;
                break;
            case GameTypeEnum.FishPrawnCrab:
                child = <FishPrawnCrabResultRecordPanel bet={bet} />;
                break;
        }

        return <Box sx={{ direction: 'ltr' }}>{child}</Box>;
    }
    return <>{t('betlog.on_going')}</>;
};
const PlaybackButton = ({ onClick }: IconButtonProps) => (
    <IconButton
        disableRipple={true}
        onClick={onClick}
        sx={{ width: '1.5rem', height: '1.5rem' }}
    >
        <PlayCircleOutlineIcon sx={{ color: '#876F4D' }} />
    </IconButton>
);
export const Detail = (props: DetailProps) => {
    const { playButtonAudio } = useContext(AudioPlayContext);
    const { externalWindowRef } = useContext(GlobalDataContext);
    const { IsCommonWallet } = useSelector((state: RootState) =>
        getMainUserState(state)
    );
    const { onGoing, bet, isFailed = false } = props;
    const { t } = useTranslation();
    const [data, setData] = useState<DetailData[]>([]);
    const [openPlayback, setOpenPlayback] = useState(false);

    const onPlaybackClickHandler = () => {
        playButtonAudio();
        if (externalWindowRef.current) {
            externalWindowRef.current.close();
            externalWindowRef.current = null;
        }
        setOpenPlayback(true);
    };

    //scroller handling
    const { isMobile } = useContext(ScreenContext);
    const startedRef = useRef<boolean>(false);
    const ref = useRef<HTMLDivElement>(null);
    const [scrollObj, setScrollObj] = useState<BScrollConstructor>();

    //initialization
    useEffect(() => {
        const data: DetailData[] = [];
        data.push({
            title: `${t('betlog.date')} / ${t('betlog.time')}`,
            content: <>{`${bet.Date} / ${bet.Time}`}</>,
        });
        data.push({
            title: `${t('betlog.game')} / ${t('betlog.round')}`,
            content: <RoundText {...props} />,
        });
        !isFailed &&
            data.push({
                title: t('betlog.win_lose'),
                content: onGoing ? (
                    <></>
                ) : (
                    <AmountText
                        amount={bet.ResultAmount / 100}
                        with_color={false}
                        disableAuto={true}
                        // fontSize={REPORT_SIZE.TEXT}
                        className="detail-list-item-text-font"
                    />
                ),
                lang_direction: false,
            });
        !isFailed &&
            data.push({
                title: t('betlog.valid_bet'),
                content: (
                    <>
                        {(bet.RollingAmount / 100).toLocaleString('en-US', {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                        })}
                    </>
                ),
            });
        isFailed
            ? data.push({
                  title: t('betlog.game_id'),
                  content: <>{bet.GameID}</>,
              })
            : data.push({
                  title: t('betlog.betId'),
                  content: <>{bet.BetID}</>,
              });
        if (!isFailed && !IsCommonWallet) {
            data.push({
                title: t('betlog.start_balance'),
                content:
                    IsCommonWallet || onGoing ? (
                        <></>
                    ) : (
                        <>
                            <AmountText
                                amount={(bet.Balance - bet.ResultAmount) / 100}
                                with_color={false}
                                disableAuto={true}
                                // fontSize={REPORT_SIZE.TEXT}
                                className="detail-list-item-text-font"
                            />
                        </>
                    ),
                lang_direction: false,
            });
        }
        data.push({
            title: t('betlog.bet_amount'),
            content: (
                <AmountText
                    amount={bet.BetAmount / 100}
                    with_color={false}
                    disableAuto={true}
                    // fontSize={REPORT_SIZE.TEXT}
                    className="detail-list-item-text-font"
                />
            ),
            lang_direction: false,
        });
        if (!isFailed && !IsCommonWallet) {
            data.push({
                title: t('betlog.balance'),
                content:
                    IsCommonWallet || onGoing ? (
                        <></>
                    ) : (
                        <AmountText
                            amount={bet.Balance / 100}
                            with_color={false}
                            disableAuto={true}
                            // fontSize={REPORT_SIZE.TEXT}
                            className="detail-list-item-text-font"
                        />
                    ),
                lang_direction: false,
            });
        }
        data.push({
            title: isFailed
                ? t('betlog.content')
                : `${t('betlog.content')} / ${t('betlog.remark')}`,
            content: <BetContentRemark {...props} />,
        });
        !isFailed &&
            data.push({
                title: t('betlog.result'),
                content: <ResultPanel {...props} />,
                lang_direction: false,
            });
        !isFailed &&
            data.push({
                title: t('betlog.playback'),
                content: <PlaybackButton onClick={onPlaybackClickHandler} />,
            });
        isFailed &&
            data.push({
                title: t('betlog.remark'),
                content: <BetRemark reason={bet.Reason} />,
            });
        setData(data);
        console.log('debug::bet::record', onGoing, bet, IsCommonWallet, props);
    }, [onGoing, bet, IsCommonWallet, props]);

    useEffect(() => {
        if (isMobile) {
            return;
        }
        if (ref && ref.current && !startedRef.current) {
            startedRef.current = true;
            const bs = BScroll(ref.current as HTMLDivElement, {
                scrollX: false,
                scrollY: true,
                click: true,
                bounce: false,
                momentum: true,
                mouseWheel: true,
                probeType: 3,
            });
            setScrollObj(bs);
        }
        if (scrollObj && ref && ref.current) {
            setTimeout(() => {
                scrollObj.refresh();
            }, 200);
        }
    }, [ref.current]);

    return (
        <>
            <Box
                className="bet-record-detail"
                overflow={'auto'}
                ref={ref}
                sx={{
                    '&::-webkit-scrollbar': {
                        display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
                    },
                    '&-ms-overflow-style': {
                        display: 'none', // Hide the scrollbar for IE
                    },
                }}
            >
                <List className="detail-list">
                    {data.map((item, idx) => (
                        <div key={`item${idx}`}>
                            {
                                <DetailResultListItem
                                    titleId={item.title}
                                    langDirection={item.lang_direction}
                                    bet={bet}
                                    onGoing={onGoing}
                                >
                                    {item.content}
                                </DetailResultListItem>
                            }
                            {idx < data.length - 1 && <DetailListDivider />}
                        </div>
                    ))}
                </List>
            </Box>
            {openPlayback && (
                <Playback
                    open={openPlayback}
                    setIsOpen={setOpenPlayback}
                    bet={bet}
                />
            )}
        </>
    );
};
type DetailListItemProps = {
    titleId: string;
    onGoing: boolean;
    onClick?: () => void;
    iconButton?: JSX.Element;
    children: JSX.Element;
    setLangDirection?: boolean;
};
const DetailListItem = (props: DetailListItemProps) => {
    const {
        titleId,
        onClick,
        iconButton,
        children,
        setLangDirection = true,
    } = props;
    return (
        <ListItem className="detail-list-item" onClick={onClick}>
            <ListItemText>
                <Stack direction={'row'} className="detail-list-item-text-root">
                    <Stack
                        direction={'row'}
                        className="detail-list-item-text-container"
                    >
                        <Typography
                            sx={{
                                color: '#BF9A5D',
                                // fontSize: REPORT_SIZE.TEXT,
                            }}
                            className="detail-list-item-text-font"
                        >
                            {titleId}
                        </Typography>
                        {iconButton}
                    </Stack>
                    <Box
                        sx={{
                            // fontSize: REPORT_SIZE.TEXT,
                            direction: `${setLangDirection ? i18n.dir(i18n.language) : ''}`,
                            textAlign: 'right',
                        }}
                        className="detail-list-item-text-font"
                    >
                        {children}
                    </Box>
                </Stack>
            </ListItemText>
        </ListItem>
    );
};

/**
 *  SA -> betLogItem.ts
 *     -> getContent
 *     -> getRemarkStr
 *  tips & gift, elno: 現時冇
 *
 *  */
const DetailContentRemark = ({
    betType,
    gameType,
    state,
}: {
    betType: number;
    gameType: number;
    state: number;
}) => {
    //TODO: other game
    const { t } = useTranslation();
    let txt: string[] = [];
    let remarkTxt: string = '';
    txt.push(t(getDetailContentKey(betType, gameType)));

    // remark
    switch (gameType) {
        default:
            switch (state) {
                case BetState.Won:
                case BetState.MWon:
                    remarkTxt = t('betlog.win');
                    break;
                case BetState.Lost:
                case BetState.MLost:
                    remarkTxt = t('betlog.lose');
                    break;
                case BetState.Refunded:
                case BetState.MRefunded:
                    remarkTxt = t('betlog.tie');
                    break;
            }
            txt.push(remarkTxt);
            break;
    }

    return <>{txt.join(' / ')}</>;
};

const DetailContent = ({
    betType,
    gameType,
}: {
    betType: number;
    gameType: number;
}) => {
    const { t } = useTranslation();
    return <>{t(getDetailContentKey(betType, gameType))}</>;
};

const getDetailContentKey = (betType: number, gameType: number) => {
    let contentTxt: string = '-';
    // content
    switch (gameType) {
        case GameTypeEnum.Baccarat:
        case GameTypeEnum.SqueezeBaccarat:
            contentTxt = `betlog.bet_bacc.${betType}`;
            break;
        case GameTypeEnum.SeDie:
            contentTxt = `betlog.bet_sedie.${betType}`;
            break;
        case GameTypeEnum.Dragon:
            contentTxt = `betlog.bet_dt.${betType}`;
            break;
        case GameTypeEnum.PokDeng:
            contentTxt = `betlog.bet_pokdeng.${betType}`;
            break;
        case GameTypeEnum.SicBo:
            contentTxt = `betlog.bet_sicbo.${betType}`;
            break;
        case GameTypeEnum.TeenPatti2020:
            contentTxt = `betlog.bet_teenpatti.${betType}`;
            break;
        case GameTypeEnum.AndarBahar:
            contentTxt = `betlog.bet_andarbahar.${betType}`;
            break;
        case GameTypeEnum.UltraRoulette:
        case GameTypeEnum.Roulette:
            contentTxt = `betlog.bet_roulette.${betType}`;
            break;
        case GameTypeEnum.ThaiHiLo:
            contentTxt = `betlog.bet_thaihilo.${betType}`;
            break;
        case GameTypeEnum.FishPrawnCrab:
            contentTxt = `betlog.bet_fishprawncrab.${betType}`;
            break;
    }

    return contentTxt;
};
